import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';
import { CircularProgress, TextField, Typography } from '@material-ui/core';

// @ts-expect-error - JS module without type declarations
import { IconDownload } from 'src/assets/icons/Download';
// @ts-expect-error - JS module without type declarations
import { CUSTOM_BUTTON_COLOR, CUSTOM_BUTTON_SIZE, CustomButton } from 'src/components/CustomButton';
// @ts-expect-error - JS module without type declarations
import { LayoutContainerCentered } from 'src/components/LayoutContainerCentered';
// @ts-expect-error - JS module without type declarations
import { exportUserReport } from 'src/graphql/reports/exportUserReport';
import {
  requestAndAwaitDashboard,
  requestAndAwaitWeeklyTrends,
  type UsageReportSnapshot,
  type UsageReportWeeklyTrend,
} from 'src/graphql/reports/queryUsageReportDashboard';
import { ROUTES } from 'src/routes';
// @ts-expect-error - JS module without type declarations
import { useErrorHandler } from 'src/services';
// @ts-expect-error - JS module without type declarations
import { useCurrentUser, useIsCurrentUserAdmin, useIsSuperAdminUser } from 'src/store';
// @ts-expect-error - JS module without type declarations
// prettier-ignore
import { useCurrentCustomer, useCurrentCustomerId, useCustomerUserReportViewEnabled } from 'src/store/customer';
// @ts-expect-error - JS module without type declarations
import { PageHeader } from 'src/views/PageHeader';

import { AdoptionChart } from './components/AdoptionChart';
import { DetailTables } from './components/DetailTables';
import { KpiCard } from './components/KpiCard';
import { TemplateUsageChart } from './components/TemplateUsageChart';
import { UserActivityChart } from './components/UserActivityChart';
import { useStyles } from './useStyles';

const DEFAULT_WEEKS = 12;

const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const toMonday = (dateStr: string): string => {
  const d = new Date(dateStr);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return formatDate(d);
};

const toSunday = (dateStr: string): string => {
  const d = new Date(dateStr);
  const day = d.getUTCDay();
  const diff = day === 0 ? 0 : 7 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return formatDate(d);
};

const getDefaultStartDate = (): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - DEFAULT_WEEKS * 7);
  return toMonday(formatDate(d));
};

const getDefaultEndDate = (): string => toSunday(formatDate(new Date()));

export const UsageReportDashboard = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const handleError = useErrorHandler();
  const customerId = useCurrentCustomerId();
  const currentUser = useCurrentUser();
  const currentCustomer = useCurrentCustomer();
  const isAdmin = useIsCurrentUserAdmin();
  const isViewEnabled = useCustomerUserReportViewEnabled();
  const isSuperAdmin = useIsSuperAdminUser();
  const isStoreLoaded = !!currentUser && !!currentCustomer;
  const hasAccess = isSuperAdmin || (isAdmin && isViewEnabled);

  const [snapshot, setSnapshot] = useState<UsageReportSnapshot | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(true);
  const [snapshotError, setSnapshotError] = useState(false);
  const [trends, setTrends] = useState<UsageReportWeeklyTrend[] | null>(null);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [startDate, setStartDate] = useState(getDefaultStartDate);
  const [endDate, setEndDate] = useState(getDefaultEndDate);
  const appliedDates = useRef({ startDate, endDate });

  const loadDashboard = useCallback(() => {
    if (!customerId || !hasAccess) return;

    setSnapshotLoading(true);
    setSnapshotError(false);
    setTrendsLoading(true);

    requestAndAwaitDashboard(customerId)
      .then((result) => {
        setSnapshot(result);
        setTrends(result.weeklyTrends);
      })
      .catch((error) => {
        setSnapshotError(true);
        handleError(error);
      })
      .finally(() => {
        setSnapshotLoading(false);
        setTrendsLoading(false);
      });
  }, [customerId, hasAccess, handleError]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (isStoreLoaded && !hasAccess) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [isStoreLoaded, hasAccess, navigate]);

  const fetchTrends = useCallback(() => {
    if (!customerId || !hasAccess) return;

    appliedDates.current = { startDate, endDate };
    setTrendsLoading(true);

    requestAndAwaitWeeklyTrends(customerId, startDate, endDate)
      .then(setTrends)
      .catch(handleError)
      .finally(() => setTrendsLoading(false));
  }, [customerId, hasAccess, handleError, startDate, endDate]);

  const hasPendingDateChange =
    startDate !== appliedDates.current.startDate || endDate !== appliedDates.current.endDate;

  const handleDownload = useCallback(async () => {
    if (!customerId) return;
    setDownloadLoading(true);
    try {
      const result = await exportUserReport(customerId);
      if (result?.content && result?.filename) {
        const byteCharacters = atob(result.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = result.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setDownloadLoading(false);
    }
  }, [customerId, handleError]);

  if (!isStoreLoaded || !hasAccess) {
    return (
      <LayoutContainerCentered>
        <div className={styles.loader}>
          <CircularProgress size={32} />
        </div>
      </LayoutContainerCentered>
    );
  }

  return (
    <LayoutContainerCentered data-testid="UsageReportDashboard">
      <PageHeader
        title="Usage dashboard"
        className={styles.pageHeader}
      />
      <div className={styles.pageContent}>
        <div className={styles.header}>
          <div />
          <CustomButton
            color={CUSTOM_BUTTON_COLOR.silver}
            size={CUSTOM_BUTTON_SIZE.small}
            onClick={handleDownload}
            loading={downloadLoading}
            disabled={downloadLoading}
            startIcon={<IconDownload />}
          >
            Download XLSX
          </CustomButton>
        </div>

        {snapshotLoading ? (
          <div className={styles.loader}>
            <CircularProgress size={32} />
          </div>
        ) : snapshotError || !snapshot ? (
          <div className={styles.error}>
            <Typography>Failed to load usage report data.</Typography>
          </div>
        ) : (
          <>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Overview</div>
              <div className={styles.kpiGrid}>
                {snapshot.general.map((item) => (
                  <KpiCard
                    key={item.metric}
                    metric={item.metric}
                    value={item.count}
                    definition={item.definition}
                  />
                ))}
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <div className={styles.sectionTitle}>Trends</div>
                <div className={styles.dateRange}>
                  <TextField
                    label="Week starting"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(toMonday(e.target.value))}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: endDate, step: 7 }}
                    className={styles.dateInput}
                  />
                  <span className={styles.dateSeparator}>to</span>
                  <TextField
                    label="Week ending"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(toSunday(e.target.value))}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: getDefaultEndDate(), step: 7 }}
                    className={styles.dateInput}
                  />
                  <CustomButton
                    color={CUSTOM_BUTTON_COLOR.blue}
                    size={CUSTOM_BUTTON_SIZE.small}
                    onClick={fetchTrends}
                    loading={trendsLoading}
                    disabled={trendsLoading || !hasPendingDateChange}
                  >
                    Apply
                  </CustomButton>
                </div>
              </div>
              <div className={styles.chartsContainer}>
                {trendsLoading && (
                  <div className={styles.chartsOverlay}>
                    <CircularProgress size={28} />
                  </div>
                )}
                <div className={styles.chartsGrid}>
                  <UserActivityChart data={trends || []} />
                  <AdoptionChart data={trends || []} />
                  <TemplateUsageChart
                    className={styles.fullWidthChart}
                    data={trends || []}
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>Details</div>
              <DetailTables data={snapshot} />
            </div>
          </>
        )}
      </div>
    </LayoutContainerCentered>
  );
};
